import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {
    filterNullishRx,
    fromControl,
    HiddenValueWhileLoading,
    TRANSLATE,
    TranslatePipe,
} from '@brunata-angular/util';
import {
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Observable,
    ReplaySubject,
    shareReplay,
    skip,
    startWith,
} from 'rxjs';
import {
    AgentConsumptionToMonitorEntry,
    AgentMonitoringFormGroup,
    consumptionToMonitorEntries,
} from './consumption-agent-monitoring.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AsyncPipe} from '@angular/common';
import {
    MetersForAgentData,
    SelectMetersForAgentFormGroup,
} from '../select-meters-for-agent/select-meters-for-agent.model';
import {SelectMetersForAgentService} from '../select-meters-for-agent/select-meters-for-agent.service';
import {SelectMetersForAgentComponent} from '../select-meters-for-agent/select-meters-for-agent.component';
import {TooltipComponent} from '@brunata-angular/ui';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {ActivatedRoute} from '@angular/router';
import {switchMap, take, withLatestFrom} from 'rxjs/operators';
import {uniqBy} from 'lodash-es';
import {ControlMessagesComponent, LoadComponent} from '@brunata-angular/ui';
import {selectBuilding} from '../../../../state/selectors';
import {Store} from '@ngrx/store';

type AgentMonitoringView = SelectMetersForAgentFormGroup['controls'] & {
    formGroup: AgentMonitoringFormGroup;
    metersForAgentData: HiddenValueWhileLoading<MetersForAgentData>;
    consumptionToMonitors: AgentConsumptionToMonitorEntry[];
    reportId: string | undefined;
};

@Component({
    selector: 'app-consumption-agent-monitoring',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        AsyncPipe,
        ControlMessagesComponent,
        TooltipComponent,
        LoadComponent,
    ],
    templateUrl: './consumption-agent-monitoring.component.html',
    styleUrl: './consumption-agent-monitoring.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentMonitoringComponent {
    private readonly store = inject(Store);
    private readonly translate = inject(TRANSLATE);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly modalService = inject(NgbModal);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly metersForAgentService = inject(SelectMetersForAgentService);

    @Input({required: true}) set formGroup(formGroup: AgentMonitoringFormGroup) {
        this.formGroupSubject$.next(formGroup);
    }

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter(buildingState => buildingState.status === 'success'),
        map(({buildingNo}) => buildingNo),
    );

    private readonly formGroupSubject$ = new ReplaySubject<AgentMonitoringFormGroup | undefined>(1);

    private readonly meterSettingsFormGroup$ = this.formGroupSubject$.pipe(
        filterNullishRx(),
        map(({controls}) => controls.selectMetersForAgentFormGroup.controls.meterSettingsFormGroup),
    );

    private readonly meterSuperTypeNoChange$ = this.meterSettingsFormGroup$.pipe(
        filterNullishRx(),
        switchMap(({controls}) =>
            fromControl(controls.meterSuperTypeNo).pipe(distinctUntilChanged()),
        ),
    );

    private readonly consumptionToMonitorChange$ = this.formGroupSubject$.pipe(
        filterNullishRx(),
        switchMap(({controls}) => fromControl(controls.consumptionToMonitor)),
        distinctUntilChanged(),
        shareReplay(1),
    );

    private readonly reportId$: Observable<string | undefined> = merge(
        this.activatedRoute.paramMap.pipe(
            map(params => params.get('reportid') ?? undefined),
            take(1),
        ),
        combineLatest([this.consumptionToMonitorChange$, this.meterSuperTypeNoChange$]).pipe(
            skip(1),
            map(() => undefined),
        ),
    ).pipe(shareReplay(1));

    private readonly formGroupChange$: Observable<AgentMonitoringFormGroup> =
        this.formGroupSubject$.pipe(
            filterNullishRx(),
            switchMap(formGroup =>
                formGroup.valueChanges.pipe(
                    startWith(formGroup),
                    map(() => formGroup),
                ),
            ),
        );

    private readonly selectedMeters$ = this.formGroupChange$.pipe(
        map(
            formGroup =>
                formGroup.getRawValue().selectMetersForAgentFormGroup.meterIdGroupsFormGroup,
        ),
        map(({idsInGroup1, idsInGroup2}) => [...idsInGroup1, ...idsInGroup2]),
    );

    private readonly metersForAgentData$: Observable<HiddenValueWhileLoading<MetersForAgentData>> =
        combineLatest([this.buildingNo$.pipe(distinctUntilChanged()), this.reportId$]).pipe(
            switchMap(([buildingNo, reportId]) =>
                this.metersForAgentService.getMetersForAgentData(buildingNo, reportId).pipe(
                    filter(data => data.loading === false),
                    withLatestFrom(this.consumptionToMonitorChange$, this.selectedMeters$),
                    map(([data, consumptionToMonitor, selectedMeters]) => ({
                        loading: false,
                        value: {
                            meters: data.value.meters.filter(
                                meter =>
                                    selectedMeters.includes(meter.meterIdAsNumber) ||
                                    meter.supportedConsumptionToMonitor[consumptionToMonitor],
                            ),
                            settings: data.value.settings,
                        },
                    })),
                ),
            ),
            startWith({loading: true as const}),
            shareReplay(1),
        );

    private readonly metersForAgent$ = this.metersForAgentData$.pipe(
        filter(data => data.loading === false),
        map(({value}) => value.meters),
    );

    readonly view$: Observable<AgentMonitoringView> = combineLatest([
        this.formGroupChange$,
        this.metersForAgentData$,
        this.reportId$,
    ]).pipe(
        map(([formGroup, metersForAgentData, reportId]) => ({
            ...formGroup.controls.selectMetersForAgentFormGroup.controls,
            formGroup,
            metersForAgentData,
            consumptionToMonitors: consumptionToMonitorEntries,
            reportId,
        })),
    );

    constructor() {
        // automatically deselect unsupported meters initially
        combineLatest([this.formGroupChange$, this.metersForAgent$])
            .pipe(take(1))
            .subscribe(([formGroup, metersForAgent]) => {
                const validMeterIds = new Set(
                    metersForAgent
                        .filter(meter => meter.meterCriteriaMismatch === false)
                        .map(m => m.meterIdAsNumber),
                );

                const {idsInGroup1, idsInGroup2} =
                    formGroup.getRawValue().selectMetersForAgentFormGroup.meterIdGroupsFormGroup;

                formGroup.controls.selectMetersForAgentFormGroup.controls.meterIdGroupsFormGroup.setValue(
                    {
                        idsInGroup1: idsInGroup1.filter(id => validMeterIds.has(id)),
                        idsInGroup2: idsInGroup2.filter(id => validMeterIds.has(id)),
                    },
                    {emitEvent: false},
                );
            });

        // set initial values if editing agent
        combineLatest([this.metersForAgent$, this.meterSettingsFormGroup$, this.reportId$])
            .pipe(take(1))
            .subscribe(([meters, {controls}]) => {
                const selectableUsageAndUnits = uniqBy(
                    meters
                        .filter(meter =>
                            controls.selectedUsageAndUnits.value.some(
                                ({unit, alloUnitType}) =>
                                    unit === meter.unit && alloUnitType === meter.alloUnitType,
                            ),
                        )
                        .map(({usageAndUnit}) => ({name: usageAndUnit, selected: true})),
                    'name',
                );

                controls.selectableUsageAndUnits.setValue(selectableUsageAndUnits);
            });

        // hide meter table when consumptionToMonitor changes from initial state
        this.consumptionToMonitorChange$
            .pipe(skip(1), distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(() => this.metersForAgentService.meterTableVisible$.next(false));
    }

    selectMeters(
        formGroup: AgentMonitoringFormGroup,
        metersForAgentData: MetersForAgentData,
    ): void {
        const modalRef = this.modalService.open(SelectMetersForAgentComponent, {
            size: 'xl',
            centered: true,
            backdrop: 'static',
            // disable esc key to close modal to prevent losing unsubmitted changes
            keyboard: false,
        });

        const {selectMetersForAgentFormGroup} = formGroup.controls;

        const component = modalRef.componentInstance as SelectMetersForAgentComponent;
        component.formGroup = selectMetersForAgentFormGroup;
        component.metersForAgentData = metersForAgentData;
        component.consumptionToMonitor = formGroup.controls.consumptionToMonitor.value;

        const initialFormValues = selectMetersForAgentFormGroup.value;

        // return to initial state if modal is closed without saving
        modalRef.dismissed.pipe(take(1)).subscribe(() => {
            selectMetersForAgentFormGroup.patchValue(initialFormValues);
            this.metersForAgentService.meterTableVisible$.next(selectMetersForAgentFormGroup.valid);
        });
    }

    async deleteMeterSettings(formGroup: AgentMonitoringFormGroup): Promise<void> {
        const confirmed = await this.showDeleteMeterSettingsDialog();

        if (!confirmed) {
            return;
        }

        this.deleteMeterSettingsState(formGroup);
    }

    async consumptionToMonitorClicked(
        event: MouseEvent,
        formGroup: AgentMonitoringFormGroup,
    ): Promise<void> {
        const {idsInGroup1, idsInGroup2} =
            formGroup.getRawValue().selectMetersForAgentFormGroup.meterIdGroupsFormGroup;

        const noMetersSelected = idsInGroup1.length === 0 && idsInGroup2.length === 0;

        if (event.cancelable === false || noMetersSelected) {
            return;
        }

        // display confirmation dialog before changing consumption to monitor
        event.preventDefault();

        const shouldDeselectMeters = await this.showDeleteMeterSettingsDialog();

        if (shouldDeselectMeters) {
            event.target?.dispatchEvent(new MouseEvent(event.type, {...event, cancelable: false}));
            return;
        }
    }

    private deleteMeterSettingsState({controls}: AgentMonitoringFormGroup): void {
        controls.selectMetersForAgentFormGroup.reset();
        this.metersForAgentService.resetState();
    }

    private async showDeleteMeterSettingsDialog(): Promise<boolean> {
        return await this.confirmationDialogService.confirm(
            this.translate('common_warning', 'online', 'common'),
            this.translate('consumption-agents_create_meter_groups_deletion'),
        );
    }
}
